import { memo, useEffect, useRef } from "react";
import { Card, Typography } from "antd";
import { motion } from "motion/react";
import type { SlideComponentProps } from "./SlideRenderer";
import { StreamingText } from "./StreamingText";
import { ANIMATION } from "./animationConfig";
import { useAnimationSequence } from "./useAnimationSequence";
import { BulletPointPlaceholders } from "types/";

export const BulletPointSlide = memo(
  ({ slide, isActive = false, onComplete }: SlideComponentProps) => {
    const placeholders = slide.placeholders as BulletPointPlaceholders;
    const title = placeholders.title;
    const bulletPoints = placeholders.bullet_points ?? [];
    const hasCalledComplete = useRef(false);

    // Steps: 0=title, 1..N=bullets
    const totalSteps = 1 + bulletPoints.length;
    const { nextStep, isStepActive, isComplete } = useAnimationSequence(
      totalSteps,
      isActive
    );

    // Notify parent when complete (once)
    useEffect(() => {
      if (isComplete && onComplete && !hasCalledComplete.current) {
        hasCalledComplete.current = true;
        onComplete();
      }
    }, [isComplete, onComplete]);

    // Not active yet - render static content
    if (!isActive) {
      return (
        <Card>
          <Typography.Title level={4}>{title}</Typography.Title>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {bulletPoints.map((point, index) => (
              <li key={index}>
                <Typography.Text>{point}</Typography.Text>
              </li>
            ))}
          </ul>
        </Card>
      );
    }

    return (
      <Card>
        {/* Step 0: Title */}
        {isStepActive(0) && (
          <Typography.Title level={4}>
            <StreamingText
              text={title}
              animate
              wordDelay={ANIMATION.WORD_DELAY}
              onComplete={nextStep}
            />
          </Typography.Title>
        )}

        {/* Steps 1-N: Bullets */}
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {bulletPoints.map((point, index) => {
            const stepIndex = index + 1;
            if (!isStepActive(stepIndex)) return null;

            return (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <Typography.Text>
                  <StreamingText
                    text={point}
                    animate
                    wordDelay={ANIMATION.WORD_DELAY}
                    onComplete={nextStep}
                  />
                </Typography.Text>
              </motion.li>
            );
          })}
        </ul>
      </Card>
    );
  }
);

BulletPointSlide.displayName = "BulletPointSlide";
